import { Collapse, Modal, Select, Spin, Upload } from "antd";
import classNames from "classnames";
import { countBy, isEmpty, isNil } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineUploadFile } from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import {
  PDF_ACCEPT,
  PDF_PHASES,
  PdfRetry,
  usePdfAnchors,
} from "../../pdf-upload";
import { applyReplacement, planReplacement } from "./replace-plan";
import { zoneLabel, zoneName } from "./zones";

const linksOf = (plan, answers) =>
  Object.fromEntries(
    plan.lost
      .map(({ zone, guess }) => {
        const answer = answers[zone.id] || {};
        if (!isNil(guess) && answer.renamed !== false) return [zone.id, guess];
        return [zone.id, answer.attach ?? null];
      })
      .filter(([, target]) => !isNil(target)),
  );

const ReplacePdf = ({ open, content, onClose, onApply }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const REPLACE_PDF = t("REPLACE_PDF", { returnObjects: true });
  const [answers, setAnswers] = useState({});
  const [applying, setApplying] = useState(false);
  const pdf = usePdfAnchors();
  const { phase, file, anchors, beforeUpload } = pdf;

  const plan = useMemo(
    () =>
      phase === PDF_PHASES.READY ? planReplacement(content, anchors) : null,
    [phase, content, anchors],
  );
  const links = isNil(plan) ? {} : linksOf(plan, answers);
  const linkedTargets = new Set(Object.values(links));
  const added = isNil(plan)
    ? []
    : plan.added.filter((zone) => !linkedTargets.has(zone.id));

  const reset = () => {
    pdf.reset();
    setAnswers({});
    setApplying(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const answer = (zoneId, patch) =>
    setAnswers((current) => ({
      ...current,
      [zoneId]: { ...current[zoneId], ...patch },
    }));

  const apply = async () => {
    setApplying(true);
    const applied = await onApply({
      file,
      apply: (current) =>
        applyReplacement({ content: current, plan, links, anchors }),
    });
    setApplying(false);
    if (applied) close();
  };

  const pick = (raw) => {
    setAnswers({});
    return beforeUpload(raw);
  };

  const failure = (message) => (
    <PdfRetry
      className="dynamic-documents-editor__replace-failure"
      message={message}
      label={REPLACE_PDF.OTHER_FILE}
      onUpload={pick}
    />
  );

  const attachOptions = (zone) => {
    const candidates = plan.added
      .filter(
        (candidate) =>
          candidate.type === zone.type &&
          (!linkedTargets.has(candidate.id) || links[zone.id] === candidate.id),
      )
      .map((candidate) => ({ candidate, label: zoneLabel(candidate, t) }));
    const counts = countBy(candidates, "label");
    return candidates.map(({ candidate, label }) => ({
      value: candidate.id,
      label: counts[label] > 1 ? `${label} · ${zoneName(candidate)}` : label,
    }));
  };

  const renamedLine = (zone, target) => {
    const before = zoneLabel(zone, t);
    const after = zoneLabel(target, t);
    if (before !== after)
      return <span>{t("REPLACE_PDF.RENAMED_LINE", { before, after })}</span>;
    return (
      <span className="dynamic-documents-editor__replace-same">
        {before}
        <span className="dynamic-documents-editor__replace-raw">
          {t("REPLACE_PDF.RENAMED_LINE", {
            before: zoneName(zone),
            after: zoneName(target),
          })}
        </span>
      </span>
    );
  };

  const lostRow = ({ zone, guess }) => {
    const current = answers[zone.id] || {};
    if (!isNil(guess) && current.renamed !== false)
      return (
        <li key={zone.id} className="dynamic-documents-editor__replace-row">
          <span className="dynamic-documents-editor__replace-tag">
            {REPLACE_PDF.RENAMED}
          </span>
          {renamedLine(
            zone,
            plan.added.find((candidate) => candidate.id === guess),
          )}
          <span className="dynamic-documents-editor__replace-actions">
            <button
              type="button"
              className="dynamic-documents-editor__chip-button dynamic-documents-editor__chip-button--active"
              onClick={() => answer(zone.id, { renamed: true })}
            >
              {REPLACE_PDF.YES}
            </button>
            <button
              type="button"
              className="dynamic-documents-editor__chip-button"
              onClick={() => answer(zone.id, { renamed: false })}
            >
              {REPLACE_PDF.NO}
            </button>
          </span>
        </li>
      );
    return (
      <li key={zone.id} className="dynamic-documents-editor__replace-row">
        <span className="dynamic-documents-editor__replace-tag dynamic-documents-editor__replace-tag--gone">
          {REPLACE_PDF.GONE}
        </span>
        <span title={t("ZONES.RAW_NAME", { name: zoneName(zone) })}>
          {zoneLabel(zone, t)}
        </span>
        <span className="dynamic-documents-editor__replace-actions">
          <Select
            size="small"
            allowClear
            className="dynamic-documents-editor__replace-attach"
            placeholder={REPLACE_PDF.ATTACH}
            value={current.attach ?? undefined}
            options={attachOptions(zone)}
            onChange={(attach) => answer(zone.id, { attach: attach ?? null })}
          />
          <button
            type="button"
            className={classNames("dynamic-documents-editor__chip-button", {
              "dynamic-documents-editor__chip-button--active": isNil(
                current.attach,
              ),
            })}
            onClick={() => answer(zone.id, { attach: null })}
          >
            {REPLACE_PDF.REMOVE}
          </button>
        </span>
      </li>
    );
  };

  const bodies = {
    [PDF_PHASES.PICK]: () => (
      <Upload.Dragger
        accept={PDF_ACCEPT}
        beforeUpload={pick}
        showUploadList={false}
        maxCount={1}
      >
        <MdOutlineUploadFile size={32} />
        <p>{REPLACE_PDF.DROP}</p>
      </Upload.Dragger>
    ),
    [PDF_PHASES.READING]: () => (
      <div className="dynamic-documents-editor__replace-reading">
        <Spin size="small" /> {REPLACE_PDF.READING}
      </div>
    ),
    [PDF_PHASES.READ_FAILED]: () => failure(REPLACE_PDF.READ_FAILED),
    [PDF_PHASES.NO_ZONE]: () => failure(REPLACE_PDF.NO_ZONE),
    [PDF_PHASES.READY]: () => (
      <div className="dynamic-documents-editor__replace">
        <p className="dynamic-documents-editor__muted">{file?.name}</p>
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: "kept",
              label: t("REPLACE_PDF.UNCHANGED", { count: plan.kept.length }),
              children: (
                <ul className="dynamic-documents-editor__replace-kept">
                  {plan.kept.map((zone) => (
                    <li
                      key={zone.id}
                      title={t("ZONES.RAW_NAME", { name: zoneName(zone) })}
                    >
                      {zoneLabel(zone, t)}
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
        {!isEmpty(plan.lost) && (
          <ul className="dynamic-documents-editor__replace-list">
            {plan.lost.map(lostRow)}
          </ul>
        )}
        {!isEmpty(added) && (
          <p className="dynamic-documents-editor__note">
            {t("REPLACE_PDF.ADDED", { count: added.length })}
          </p>
        )}
      </div>
    ),
  };

  return (
    <Modal
      open={open}
      centered
      width={640}
      title={REPLACE_PDF.TITLE}
      onCancel={close}
      closable={!applying}
      maskClosable={!applying}
      footer={
        <div className="dynamic-documents-editor__sheet-footer">
          <Button type="gray--link" onClick={close} disabled={applying}>
            {REPLACE_PDF.CANCEL}
          </Button>
          <Button
            type="primary"
            loading={applying}
            disabled={phase !== PDF_PHASES.READY}
            onClick={apply}
          >
            {REPLACE_PDF.APPLY}
          </Button>
        </div>
      }
    >
      {bodies[phase]()}
    </Modal>
  );
};

export default ReplacePdf;
